import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FaEye, FaPrint, FaFileExcel, FaFilePdf, FaTimes } from 'react-icons/fa';
import { getQuotationsReport, clearQuotationsReport, clearError, clearSuccess, getCustomersForProjectReport, getProjectsForReport } from '../../../../redux/Slices/ProjectReportsSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import i18n from '../../../../i18n/i18n';
import { getPdfRowStyles, chunkArray, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';

const applySheetDirection = (worksheet, rtl = false) => {
  if (!worksheet || !worksheet['!ref']) return;
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  for (let R = range.s.r; R <= range.e.r; R += 1) {
    for (let C = range.s.c; C <= range.e.c; C += 1) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (!cell) continue;
      const alignment = rtl ? 'right' : 'left';
      cell.s = {
        ...(cell.s || {}),
        alignment: { ...(cell.s?.alignment || {}), horizontal: alignment },
      };
    }
  }
};

const writeWorkbook = (workbook, filename) => {
  XLSX.writeFile(workbook, filename, { cellStyles: true });
};

const QuotationsReport = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const headerAlign = isRTL ? 'right' : 'left';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const { quotationsReport, customers, projects, isLoading, error, success, customersPagination, projectsPagination } = useSelector((state) => state.projectReports);

  const getCustomerName = (customer) => {
    if (!customer) return '';
    return isRTL
      ? (customer.name_ar || customer.name_en || customer.name || '')
      : (customer.name_en || customer.name || customer.name_ar || '');
  };

  const getProjectName = (project) => {
    if (!project) return '';
    return isRTL
      ? (project.name_ar || project.name_en || project.name || '')
      : (project.name_en || project.name || project.name_ar || '');
  };

  const translateStatus = (statusValue) => {
    if (!statusValue) return '—';
    switch (statusValue) {
      case 'draft':
        return t('projectReports.draft', { defaultValue: 'مسودة' });
      case 'sent':
        return t('projectReports.sent', { defaultValue: 'مرسل' });
      case 'sent_to_client':
        return t('projectReports.sentToClient', { defaultValue: 'مرسل للعميل' });
      case 'approved':
        return t('projectReports.approved', { defaultValue: 'موافق عليه' });
      case 'rejected':
        return t('projectReports.rejected', { defaultValue: 'مرفوض' });
      case 'converted':
        return t('projectReports.converted', { defaultValue: 'محول' });
      default:
        return statusValue;
    }
  };

  const [customerId, setCustomerId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [minTotal, setMinTotal] = useState('');
  const [maxTotal, setMaxTotal] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerPage, setCustomerPage] = useState(1);
  const customerSearchInputRef = useRef(null);

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectPage, setProjectPage] = useState(1);
  const projectSearchInputRef = useRef(null);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const pageSize = 10;
  const customersHasNext = customersPagination && customersPagination.current_page < customersPagination.last_page;
  const projectsHasNext = projectsPagination && projectsPagination.current_page < projectsPagination.last_page;
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    setTitle('projectReports.quotations', { defaultValue: 'تقارير عروض الأسعار' });
    dispatch(getCustomersForProjectReport({ page: 1, per_page: 10 }));
    dispatch(getProjectsForReport({ page: 1, per_page: 10 }));
    dispatch(getQuotationsReport({}));
    return () => {
      dispatch(clearQuotationsReport());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      if (isInitialLoad) {
        setIsInitialLoad(false);
        dispatch(clearSuccess());
        return;
      }
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL, isInitialLoad]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (customerSearchTerm) {
        dispatch(getCustomersForProjectReport({ search: customerSearchTerm, page: 1, per_page: pageSize }));
        setCustomerPage(1);
      } else {
        dispatch(getCustomersForProjectReport({ page: customerPage, per_page: pageSize }));
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [customerSearchTerm, dispatch, pageSize]);

  useEffect(() => {
    if (customerDropdownOpen && !customerSearchTerm) {
      dispatch(getCustomersForProjectReport({ page: customerPage, per_page: pageSize }));
    }
  }, [customerPage, customerDropdownOpen, customerSearchTerm, dispatch, pageSize]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (projectSearchTerm) {
        dispatch(getProjectsForReport({ search: projectSearchTerm, page: 1, per_page: pageSize }));
        setProjectPage(1);
      } else {
        dispatch(getProjectsForReport({ page: projectPage, per_page: pageSize }));
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [projectSearchTerm, dispatch, pageSize]);

  useEffect(() => {
    if (projectDropdownOpen && !projectSearchTerm) {
      dispatch(getProjectsForReport({ page: projectPage, per_page: pageSize }));
    }
  }, [projectPage, projectDropdownOpen, projectSearchTerm, dispatch, pageSize]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerDropdownOpen && !e.target.closest('.customer-dropdown-container')) {
        setCustomerDropdownOpen(false);
      }
      if (projectDropdownOpen && !e.target.closest('.project-dropdown-container')) {
        setProjectDropdownOpen(false);
      }
      if (statusDropdownOpen && !e.target.closest('.status-dropdown-container')) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [customerDropdownOpen, projectDropdownOpen, statusDropdownOpen]);

  const filteredCustomers = useMemo(() => {
    if (!Array.isArray(customers)) return [];
    if (!customerSearchTerm) return customers;
    const term = customerSearchTerm.toLowerCase();
    return customers.filter((customer) => {
      const nameAr = customer.name_ar?.toLowerCase() || '';
      const nameEn = customer.name_en?.toLowerCase() || '';
      const name = customer.name?.toLowerCase() || '';
      const code = customer.code?.toLowerCase() || '';
      return nameAr.includes(term) || nameEn.includes(term) || name.includes(term) || code.includes(term);
    });
  }, [customers, customerSearchTerm]);

  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    if (!projectSearchTerm) return projects;
    const term = projectSearchTerm.toLowerCase();
    return projects.filter((project) => {
      const nameAr = project.name_ar?.toLowerCase() || '';
      const nameEn = project.name_en?.toLowerCase() || '';
      const name = project.name?.toLowerCase() || '';
      const code = project.code?.toLowerCase() || '';
      return nameAr.includes(term) || nameEn.includes(term) || name.includes(term) || code.includes(term);
    });
  }, [projects, projectSearchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = {};
    if (customerId) params.customer_id = customerId;
    if (projectId) params.project_id = projectId;
    if (status) params.status = status;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (minTotal) params.min_total = minTotal;
    if (maxTotal) params.max_total = maxTotal;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    dispatch(getQuotationsReport(params));
  };

  const handlePreviewAll = () => {
    if (!quotationsReport) {
      toast.error(t('projectReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    setIsPreviewOpen(true);
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  const handlePrintAll = async () => {
    let originalQuotationsReport = null;
    try {
      setIsGeneratingPdf(true);

      // Get current report data to extract total
      const currentData = quotationsReport?.data || quotationsReport || {};
      const currentQuotations = currentData.data || currentData.quotations || [];
      const total = currentData.total || currentData.meta?.total || currentQuotations.length;

      if (total === 0 || currentQuotations.length === 0) {
        toast.error(t('projectReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
        setIsGeneratingPdf(false);
        return;
      }

      // Fetch all quotations with per_page = total
      const params = {
        page: 1,
        per_page: total,
      };
      if (customerId) params.customer_id = customerId;
      if (projectId) params.project_id = projectId;
      if (status) params.status = status;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (minTotal) params.min_total = minTotal;
      if (maxTotal) params.max_total = maxTotal;
      if (sortBy) params.sort_by = sortBy;
      if (sortOrder) params.sort_order = sortOrder;

      const result = await dispatch(getQuotationsReport(params));
      if (!getQuotationsReport.fulfilled.match(result)) {
        toast.error(t('projectReports.printError', { defaultValue: 'فشل تحميل البيانات' }), { rtl: isRTL });
        setIsGeneratingPdf(false);
        return;
      }

      const allData = result.payload?.data || result.payload || {};
      const allQuotations = allData.data || allData.quotations || [];

      if (!allQuotations || allQuotations.length === 0) {
        toast.error(t('projectReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
        setIsGeneratingPdf(false);
        return;
      }

      // Temporarily set all quotations for PDF generation
      originalQuotationsReport = quotationsReport;

      // Split data into chunks of 20 items per page
      const chunks = chunkArray(allQuotations, 20);
      setPdfChunks(chunks);

      // Create temporary report data with first chunk
      const tempReport = {
        ...allData,
        data: chunks[0] || [],
        quotations: chunks[0] || []
      };
      // We'll need to update the quotationsReport state temporarily
      // But since it's from Redux, we'll handle it differently in the template

      setPdfType('all');
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 200));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      // Generate PDF for each chunk
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        // Wait for DOM update
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const element = document.getElementById(`quotations-report-pdf-all-${chunkIndex}`);
        if (!element) {
          if (chunkIndex === 0) {
            setPdfChunks([]);
            toast.error(t('projectReports.printError', { defaultValue: 'قالب الطباعة غير متوفر' }), { rtl: isRTL });
            setIsGeneratingPdf(false);
            setPdfType(null);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.getElementById(`quotations-report-pdf-all-${chunkIndex}`);
            if (clonedElement) {
              clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = 'bold';
              clonedElement.style.direction = dir;
            }
          }
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        // Add the image to the current page
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
      }

      pdf.save(`ACE_QuotationsReport_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t('projectReports.printed', { defaultValue: 'تم طباعة التقرير بنجاح' }), { rtl: isRTL });

      // Restore original report
      setPdfChunks([]);
    } catch (error) {
      console.error('Quotations Report PDF error:', error);
      toast.error(t('projectReports.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!quotationsReport) {
      toast.error(t('projectReports.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const data = quotationsReport?.data || quotationsReport || {};
      const quotations = data.data || data.quotations || [];

      const excelData = [
        {
          [t('projectReports.period', { defaultValue: 'الفترة' })]: `${formatDate(fromDate)} - ${formatDate(toDate)}`,
        },
        {},
        ...quotations.map((quotation) => ({
          [t('projectReports.quotationNo', { defaultValue: 'رقم العرض' })]: quotation.quotation_no || '—',
          [t('projectReports.date', { defaultValue: 'التاريخ' })]: formatDate(quotation.created_at),
          [t('projectReports.customer', { defaultValue: 'العميل' })]: isRTL ? (quotation.customer?.name_ar || quotation.customer?.name) : (quotation.customer?.name || quotation.customer?.name_ar),
          [t('projectReports.project', { defaultValue: 'المشروع' })]: isRTL
            ? (quotation.project?.name_ar || quotation.project?.name_en || quotation.project?.name)
            : (quotation.project?.name_en || quotation.project?.name || quotation.project?.name_ar),
          [t('projectReports.status', { defaultValue: 'الحالة' })]: translateStatus(quotation.status),
          [t('projectReports.total', { defaultValue: 'الإجمالي' })]: formatNumber(quotation.total_amount || 0),
        })),
      ];

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      applySheetDirection(worksheet, isRTL);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Quotations Report');
      writeWorkbook(workbook, `QuotationsReport_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('projectReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('projectReports.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatNumber = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const reportData = quotationsReport?.data || quotationsReport || {};
  const quotations = reportData.data || reportData.quotations || [];
  const summary = reportData.summary || {};
  const filtersApplied = reportData.filters_applied || {};
  const selectedCustomer = Array.isArray(customers) ? customers.find(c => String(c.id) === String(customerId)) : null;
  const selectedProject = Array.isArray(projects) ? projects.find(p => String(p.id) === String(projectId)) : null;

  return (
    <div style={{ padding: '20px', direction: dir }}>
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <h2 style={{ color: 'white', marginBottom: '20px', textAlign: headerAlign }}>
          {t('projectReports.quotations', { defaultValue: 'تقارير عروض الأسعار' })}
        </h2>

        {/* Filters */}
        <form onSubmit={handleSubmit} style={{
          backgroundColor: '#1a1f2e',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div style={{ position: 'relative' }} className="customer-dropdown-container">
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.customer', { defaultValue: 'العميل' })}
              </label>
              <div
                onClick={() => {
                  setCustomerDropdownOpen(!customerDropdownOpen);
                  if (!customerDropdownOpen) {
                    setTimeout(() => {
                      if (customerSearchInputRef.current) {
                        customerSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textAlign: headerAlign,
                  direction: dir
                }}
              >
                <span>
                  {selectedCustomer ? getCustomerName(selectedCustomer) : t('projectReports.selectCustomer', { defaultValue: 'اختر العميل' })}
                </span>
                <span>▼</span>
              </div>
              {customerDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #333' }}>
                    <input
                      ref={customerSearchInputRef}
                      type="text"
                      placeholder={t('common.search', { defaultValue: isRTL ? 'بحث...' : 'Search...' })}
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: '#0d1117',
                        border: 'none',
                        borderBottom: '1px solid #333',
                        color: 'white',
                        borderRadius: '6px 6px 0 0',
                        textAlign: headerAlign,
                        direction: dir
                      }}
                    />
                  </div>
                  <div style={{ padding: '8px', borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomerPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={customerPage === 1}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: customerPage === 1 ? '#666' : '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: customerPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ‹
                    </button>
                    <span style={{ color: '#ccc', fontSize: '12px', flex: 1, textAlign: 'center' }}>
                      {t('projectReports.page', { defaultValue: 'صفحة' })} {customerPage} {customersPagination?.last_page ? ` / ${customersPagination.last_page}` : ''}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomerPage(prev => prev + 1);
                      }}
                      disabled={!customersHasNext}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: customersHasNext ? '#AC2000' : '#666',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: customersHasNext ? 'pointer' : 'not-allowed',
                        fontSize: '12px'
                      }}
                    >
                      ›
                    </button>
                  </div>
                  {filteredCustomers.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
                      {t('projectReports.noData', { defaultValue: 'لا توجد بيانات' })}
                    </div>
                  ) : (
                    <>
                      {filteredCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          onClick={() => {
                            setCustomerId(String(customer.id));
                            setCustomerDropdownOpen(false);
                            setCustomerSearchTerm('');
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #333',
                            color: 'white',
                            backgroundColor: String(customerId) === String(customer.id) ? '#2a2a2a' : 'transparent',
                            textAlign: headerAlign,
                            direction: dir
                          }}
                        >
                          {customer.code ? `${customer.code} - ` : ''}{getCustomerName(customer)}
                        </div>
                      ))}
                      <div style={{ padding: '10px' }}></div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div style={{ position: 'relative' }} className="project-dropdown-container">
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.project', { defaultValue: 'المشروع' })}
              </label>
              <div
                onClick={() => {
                  setProjectDropdownOpen(!projectDropdownOpen);
                  if (!projectDropdownOpen) {
                    setTimeout(() => {
                      if (projectSearchInputRef.current) {
                        projectSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textAlign: headerAlign,
                  direction: dir
                }}
              >
                <span>
                  {selectedProject ? getProjectName(selectedProject) : t('projectReports.selectProject', { defaultValue: 'اختر المشروع' })}
                </span>
                <span>▼</span>
              </div>
              {projectDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #333' }}>
                    <input
                      ref={projectSearchInputRef}
                      type="text"
                      placeholder={t('common.search', { defaultValue: isRTL ? 'بحث...' : 'Search...' })}
                      value={projectSearchTerm}
                      onChange={(e) => setProjectSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: '#0d1117',
                        border: 'none',
                        borderBottom: '1px solid #333',
                        color: 'white',
                        borderRadius: '6px 6px 0 0',
                        textAlign: headerAlign,
                        direction: dir
                      }}
                    />
                  </div>
                  {filteredProjects.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
                      {t('projectReports.noData', { defaultValue: 'لا توجد بيانات' })}
                    </div>
                  ) : (
                    <>
                      {filteredProjects.map((project) => (
                        <div
                          key={project.id}
                          onClick={() => {
                            setProjectId(String(project.id));
                            setProjectDropdownOpen(false);
                            setProjectSearchTerm('');
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #333',
                            color: 'white',
                            backgroundColor: String(projectId) === String(project.id) ? '#2a2a2a' : 'transparent',
                            textAlign: headerAlign,
                            direction: dir
                          }}
                        >
                          {project.code ? `${project.code} - ` : ''}{getProjectName(project)}
                        </div>
                      ))}
                      <div style={{ padding: '10px' }}></div>
                    </>
                  )}
                  <div style={{ padding: '10px', borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={projectPage === 1}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: projectPage === 1 ? '#666' : '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: projectPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ‹
                    </button>
                    <span style={{ color: '#ccc', fontSize: '12px', flex: 1, textAlign: 'center' }}>
                      {t('projectReports.page', { defaultValue: 'صفحة' })} {projectPage} {projectsPagination?.last_page ? ` / ${projectsPagination.last_page}` : ''}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectPage(prev => prev + 1);
                      }}
                      disabled={!projectsHasNext}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: projectsHasNext ? '#AC2000' : '#666',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: projectsHasNext ? 'pointer' : 'not-allowed',
                        fontSize: '12px'
                      }}
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ position: 'relative' }} className="status-dropdown-container">
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.status', { defaultValue: 'الحالة' })}
              </label>
              <div
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textAlign: headerAlign,
                  direction: dir
                }}
              >
                <span>
                  {status ? (
                    translateStatus(status)
                  ) : t('projectReports.selectStatus', { defaultValue: 'اختر الحالة' })}
                </span>
                <span>▼</span>
              </div>
              {statusDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  {[
                    { value: 'draft', label: t('projectReports.draft', { defaultValue: 'مسودة' }) },
                    { value: 'sent', label: t('projectReports.sent', { defaultValue: 'مرسل' }) },
                    { value: 'sent_to_client', label: t('projectReports.sentToClient', { defaultValue: 'مرسل للعميل' }) },
                    { value: 'approved', label: t('projectReports.approved', { defaultValue: 'موافق عليه' }) },
                    { value: 'rejected', label: t('projectReports.rejected', { defaultValue: 'مرفوض' }) },
                    { value: 'converted', label: t('projectReports.converted', { defaultValue: 'محول' }) }
                  ].map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setStatus(option.value);
                        setStatusDropdownOpen(false);
                      }}
                      style={{
                        padding: '10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #333',
                        color: 'white',
                        backgroundColor: status === option.value ? '#2a2a2a' : 'transparent',
                        textAlign: headerAlign,
                        direction: dir
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.fromDate', { defaultValue: 'من تاريخ' })}
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.toDate', { defaultValue: 'إلى تاريخ' })}
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.minTotal', { defaultValue: 'الحد الأدنى للمبلغ' })}
              </label>
              <input
                type="number"
                value={minTotal}
                onChange={(e) => setMinTotal(e.target.value)}
                placeholder={t('projectReports.minTotal', { defaultValue: 'الحد الأدنى' })}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.maxTotal', { defaultValue: 'الحد الأقصى للمبلغ' })}
              </label>
              <input
                type="number"
                value={maxTotal}
                onChange={(e) => setMaxTotal(e.target.value)}
                placeholder={t('projectReports.maxTotal', { defaultValue: 'الحد الأقصى' })}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.sortBy', { defaultValue: 'ترتيب حسب' })}
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              >
                <option value="created_at">{t('projectReports.createdAt', { defaultValue: 'تاريخ الإنشاء' })}</option>
                <option value="date">{t('projectReports.date', { defaultValue: 'التاريخ' })}</option>
                <option value="total">{t('projectReports.total', { defaultValue: 'الإجمالي' })}</option>
              </select>
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', textAlign: headerAlign }}>
                {t('projectReports.sortOrder', { defaultValue: 'ترتيب' })}
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              >
                <option value="asc">{t('projectReports.asc', { defaultValue: 'تصاعدي' })}</option>
                <option value="desc">{t('projectReports.desc', { defaultValue: 'تنازلي' })}</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? t('projectReports.loading', { defaultValue: 'جاري التحميل...' }) : t('projectReports.viewReport', { defaultValue: 'عرض التقرير' })}
            </button>
          </div>
        </form>

        {/* Action Buttons */}
        {quotationsReport && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#444',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaEye /> {t('projectReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('projectReports.generating', { defaultValue: 'جاري التوليد...' }) : t('projectReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaFileExcel /> {t('projectReports.exportAllExcel', { defaultValue: 'تنزيل Excel للكل' })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {quotationsReport && (
          <div style={{
            backgroundColor: '#1a1f2e',
            padding: '20px',
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <div style={{ color: '#ccc', marginBottom: '20px' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('projectReports.period', { defaultValue: 'الفترة' })}:</strong> {formatDate(fromDate)} - {formatDate(toDate)}
              </div>
            </div>

            {/* Summary Section */}
            {summary && Object.keys(summary).length > 0 && (
              <div style={{
                backgroundColor: '#1b3350',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #333',
                textAlign: headerAlign,
                direction: dir
              }}>
                <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', textAlign: headerAlign }}>
                  {t('projectReports.summary', { defaultValue: 'ملخص التقرير' })}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {summary.total_count !== undefined && (
                    <div style={{ backgroundColor: '#1a1f2e', padding: '15px', borderRadius: '6px', border: '1px solid #333', textAlign: headerAlign, direction: dir }}>
                      <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px' }}>
                        {t('projectReports.totalCount', { defaultValue: 'إجمالي عدد العروض' })}
                      </div>
                      <div style={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>
                        {summary.total_count || 0}
                      </div>
                    </div>
                  )}
                  {summary.total_value !== undefined && (
                    <div style={{ backgroundColor: '#1a1f2e', padding: '15px', borderRadius: '6px', border: '1px solid #333', textAlign: headerAlign, direction: dir }}>
                      <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px' }}>
                        {t('projectReports.totalValue', { defaultValue: 'إجمالي القيمة' })}
                      </div>
                      <div style={{ color: '#17a2b8', fontSize: '20px', fontWeight: 'bold' }}>
                        {formatNumber(summary.total_value || 0)}
                      </div>
                    </div>
                  )}
                  {summary.average_value !== undefined && (
                    <div style={{ backgroundColor: '#1a1f2e', padding: '15px', borderRadius: '6px', border: '1px solid #333', textAlign: headerAlign, direction: dir }}>
                      <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px' }}>
                        {t('projectReports.averageValue', { defaultValue: 'متوسط القيمة' })}
                      </div>
                      <div style={{ color: '#28a745', fontSize: '20px', fontWeight: 'bold' }}>
                        {formatNumber(summary.average_value || 0)}
                      </div>
                    </div>
                  )}
                </div>

                {/* By Status Summary */}
                {summary.by_status && Object.keys(summary.by_status).length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h4 style={{ color: 'white', marginBottom: '10px', fontSize: '16px', textAlign: headerAlign }}>
                      {t('projectReports.byStatus', { defaultValue: 'حسب الحالة' })}
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                      {Object.entries(summary.by_status).map(([status, statusData]) => (
                        <div key={status} style={{ backgroundColor: '#1a1f2e', padding: '12px', borderRadius: '6px', border: '1px solid #333', textAlign: headerAlign, direction: dir }}>
                          <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '5px', textTransform: 'capitalize' }}>
                            {translateStatus(status)}
                          </div>
                          <div style={{ color: 'white', fontSize: '14px', marginBottom: '3px' }}>
                            {t('projectReports.count', { defaultValue: 'العدد' })}: <strong>{statusData.count || 0}</strong>
                          </div>
                          <div style={{ color: '#17a2b8', fontSize: '14px' }}>
                            {t('projectReports.total', { defaultValue: 'الإجمالي' })}: <strong>{formatNumber(statusData.total || 0)}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {quotations && quotations.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#1b3350' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#AC2000' }}>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                        {t('projectReports.quotationNo', { defaultValue: 'رقم العرض' })}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                        {t('projectReports.date', { defaultValue: 'التاريخ' })}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                        {t('projectReports.customer', { defaultValue: 'العميل' })}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                        {t('projectReports.project', { defaultValue: 'المشروع' })}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                        {t('projectReports.status', { defaultValue: 'الحالة' })}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                        {t('projectReports.total', { defaultValue: 'الإجمالي' })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotations.map((quotation, idx) => (
                      <tr key={quotation.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e' }}>
                        <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {quotation.quotation_no || '—'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {formatDate(quotation.created_at)}
                        </td>
                        <td style={{ padding: '12px', textAlign: isRTL ? 'right' : 'left', color: 'white', border: '1px solid #333' }}>
                          {isRTL ? (quotation.customer?.name_ar || quotation.customer?.name) : (quotation.customer?.name || quotation.customer?.name_ar) || '—'}
                        </td>
                        <td style={{ padding: '12px', textAlign: isRTL ? 'right' : 'left', color: 'white', border: '1px solid #333' }}>
                          {isRTL
                            ? (quotation.project?.name_ar || quotation.project?.name_en || quotation.project?.name)
                            : (quotation.project?.name_en || quotation.project?.name || quotation.project?.name_ar) || '—'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {translateStatus(quotation.status)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {formatNumber(quotation.total_amount || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '30px', textAlign: 'center', color: '#9ca3af' }}>
                {t('projectReports.noData', { defaultValue: 'لا توجد بيانات' })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* PDF Print Template */}
      {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && (
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`quotations-report-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '794px',
              height: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              overflow: 'visible'
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              height: '1043px',
              overflow: 'visible'
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                direction: dir,
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      marginBottom: '8px',
                      borderBottom: '4px double #000',
                      paddingBottom: '8px',
                      flexShrink: 0
                    }}
                    crossOrigin="anonymous"
                  />

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '8px' }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: '0 0 4px' }}>
                        {t('projectReports.quotations', { defaultValue: 'تقارير عروض الأسعار' })}
                      </h2>
                      <p style={{ fontSize: '12px', margin: '0 0 4px', color: '#333', fontFamily: '"Times New Roman", serif', fontWeight: 'normal' }}>
                        {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                      {(customerId || projectId || status || fromDate || toDate || minTotal || maxTotal) && (
                        <div style={{ fontSize: '11px', color: '#555', direction: dir, fontFamily: '"Times New Roman", serif', fontWeight: 'normal' }}>
                          {[
                            customerId && customers && customers.find(c => String(c.id) === String(customerId)) && `${t('projectReports.customer', { defaultValue: 'العميل' })}: ${getCustomerName(customers.find(c => String(c.id) === String(customerId)))}`,
                            projectId && projects && projects.find(p => String(p.id) === String(projectId)) && `${t('projectReports.project', { defaultValue: 'المشروع' })}: ${getProjectName(projects.find(p => String(p.id) === String(projectId)))}`,
                            status && `${t('projectReports.status', { defaultValue: 'الحالة' })}: ${translateStatus(status)}`,
                            fromDate && `${t('projectReports.from', { defaultValue: 'من' })}: ${formatDate(fromDate)}`,
                            toDate && `${t('projectReports.to', { defaultValue: 'إلى' })}: ${formatDate(toDate)}`,
                            minTotal && `${t('projectReports.minTotal', { defaultValue: 'الحد الأدنى' })}: ${minTotal}`,
                            maxTotal && `${t('projectReports.maxTotal', { defaultValue: 'الحد الأقصى' })}: ${maxTotal}`,
                          ].filter(Boolean).join(' | ')}
                        </div>
                      )}
                    </div>
                    <div style={{ color: '#000', fontSize: '12px' }}>
                      {quotations && quotations.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: '15px' }}>
                          <thead>
                            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '36px' }}>#</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.quotationNo', { defaultValue: 'رقم العرض' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.date', { defaultValue: 'التاريخ' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.customer', { defaultValue: 'العميل' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.project', { defaultValue: 'المشروع' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.status', { defaultValue: 'الحالة' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.total', { defaultValue: 'الإجمالي' })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((quotation, idx) => (
                              <tr key={quotation.id || idx} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '36px' }}>{chunkIndex * 20 + idx + 1}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {quotation.quotation_no || '—'}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {formatDate(quotation.created_at)}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>
                                  {isRTL ? (quotation.customer?.name_ar || quotation.customer?.name) : (quotation.customer?.name || quotation.customer?.name_ar) || '—'}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>
                                  {isRTL
                                    ? (quotation.project?.name_ar || quotation.project?.name_en || quotation.project?.name)
                                    : (quotation.project?.name_en || quotation.project?.name || quotation.project?.name_ar) || '—'}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {translateStatus(quotation.status)}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {formatNumber(quotation.total_amount || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          {t('projectReports.noData', { defaultValue: 'لا توجد بيانات' })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '18px',
                    right: '18px',
                    flexShrink: 0
                  }}>
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                          borderTop: '4px double #000',
                          paddingTop: '8px',
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Preview Modal */}
      {isPreviewOpen && quotationsReport && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '20px',
            overflow: 'auto',
            boxSizing: 'border-box'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsPreviewOpen(false);
            }
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              display: 'flex',
              gap: '10px',
              zIndex: 1000000
            }}
          >
            <button
              onClick={() => setPreviewZoom(prev => Math.min(2, prev + 0.1))}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              +
            </button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '50px',
              padding: '10px 15px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              {Math.round(previewZoom * 100)}%
            </div>
            <button
              onClick={() => setPreviewZoom(prev => Math.max(0.1, prev - 0.1))}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              −
            </button>
            <button
              onClick={() => {
                setPreviewZoom(1.0);
                setPreviewPosition({ x: 0, y: 0 });
              }}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {t('common.reset', { defaultValue: 'إعادة تعيين' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaFilePdf /> {t('projectReports.printPDF', { defaultValue: 'طباعة PDF' })}
            </button>
            <button
              onClick={() => setIsPreviewOpen(false)}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              <FaTimes />
            </button>
          </div>

          <div
            id="preview-quotations-report-pdf-all"
            style={{
              transform: `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`,
              transformOrigin: 'center center',
              width: '794px',
              minHeight: '1123px',
              height: 'auto',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              cursor: isDragging ? 'grabbing' : 'grab',
              transition: isDragging ? 'none' : 'transform 0.1s',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
            onMouseDown={(e) => {
              if (e.button === 0) {
                setIsDragging(true);
                setDragStart({
                  x: e.clientX - previewPosition.x,
                  y: e.clientY - previewPosition.y
                });
              }
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '1043px',
              height: 'auto',
              overflow: 'visible'
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                direction: dir,
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      marginBottom: '8px',
                      borderBottom: '4px double #000',
                      paddingBottom: '8px',
                      flexShrink: 0
                    }}
                    crossOrigin="anonymous"
                  />

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '8px' }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: '0 0 4px' }}>
                        {t('projectReports.quotations', { defaultValue: 'تقارير عروض الأسعار' })}
                      </h2>
                      <p style={{ fontSize: '12px', margin: '0 0 4px', color: '#333', fontFamily: '"Times New Roman", serif', fontWeight: 'normal' }}>
                        {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                      {(customerId || projectId || status || fromDate || toDate || minTotal || maxTotal) && (
                        <div style={{ fontSize: '11px', color: '#555', direction: dir, fontFamily: '"Times New Roman", serif', fontWeight: 'normal' }}>
                          {[
                            customerId && customers && customers.find(c => String(c.id) === String(customerId)) && `${t('projectReports.customer', { defaultValue: 'العميل' })}: ${getCustomerName(customers.find(c => String(c.id) === String(customerId)))}`,
                            projectId && projects && projects.find(p => String(p.id) === String(projectId)) && `${t('projectReports.project', { defaultValue: 'المشروع' })}: ${getProjectName(projects.find(p => String(p.id) === String(projectId)))}`,
                            status && `${t('projectReports.status', { defaultValue: 'الحالة' })}: ${translateStatus(status)}`,
                            fromDate && `${t('projectReports.from', { defaultValue: 'من' })}: ${formatDate(fromDate)}`,
                            toDate && `${t('projectReports.to', { defaultValue: 'إلى' })}: ${formatDate(toDate)}`,
                            minTotal && `${t('projectReports.minTotal', { defaultValue: 'الحد الأدنى' })}: ${minTotal}`,
                            maxTotal && `${t('projectReports.maxTotal', { defaultValue: 'الحد الأقصى' })}: ${maxTotal}`,
                          ].filter(Boolean).join(' | ')}
                        </div>
                      )}
                    </div>
                    <div style={{ color: '#000', fontSize: '12px' }}>
                      {quotations && quotations.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: '15px' }}>
                          <thead>
                            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)', fontWeight: 'bold' }}>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '36px' }}>#</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.quotationNo', { defaultValue: 'رقم العرض' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.date', { defaultValue: 'التاريخ' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.customer', { defaultValue: 'العميل' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.project', { defaultValue: 'المشروع' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.status', { defaultValue: 'الحالة' })}
                              </th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                {t('projectReports.total', { defaultValue: 'الإجمالي' })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotations.map((quotation, idx) => (
                              <tr key={quotation.id || idx} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '36px' }}>{idx + 1}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {quotation.quotation_no || '—'}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {formatDate(quotation.created_at)}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>
                                  {isRTL ? (quotation.customer?.name_ar || quotation.customer?.name) : (quotation.customer?.name || quotation.customer?.name_ar) || '—'}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left' }}>
                                  {isRTL
                                    ? (quotation.project?.name_ar || quotation.project?.name_en || quotation.project?.name)
                                    : (quotation.project?.name_en || quotation.project?.name || quotation.project?.name_ar) || '—'}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {translateStatus(quotation.status)}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                                  {formatNumber(quotation.total_amount || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          {t('projectReports.noData', { defaultValue: 'لا توجد بيانات' })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '18px',
                    right: '18px',
                    flexShrink: 0
                  }}>
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                          borderTop: '4px double #000',
                          paddingTop: '8px',
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default QuotationsReport;


